import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/context";
import { favoritesService } from "../services/favoritesService";
import { API_CONFIG } from "../config/environment";

// 🔥 SOLUCIÓN: Usar window para persistir entre hot-reloads
// Inicializar o recuperar el estado global
if (!window.__FAVORITES_CACHE__) {
  window.__FAVORITES_CACHE__ = {
    localFavoritesCache: new Map(),
    remoteFavoritesCache: new Map(),
    pendingSyncQueue: new Set(),
    pendingRemoveQueue: new Set(),
    lastRemoteSync: 0,
    lastLocalModification: 0,
    initialized: false,
  };

  // 🔄 Cargar desde localStorage al inicializar el módulo
  try {
    const savedFavorites = localStorage.getItem("tattersall-favorites");
    const savedMetadata = localStorage.getItem("tattersall-favorites-metadata");

    if (savedFavorites && savedMetadata) {
      const favoritesArray = JSON.parse(savedFavorites);
      const metadata = JSON.parse(savedMetadata);

      // Restaurar favoritos al cache
      favoritesArray.forEach((fav) => {
        const productId = fav.productId.toString();
        const favoriteData = {
          id: fav.favoriteId,
          productId: fav.productId,
          isFavorite: true,
          addedAt: fav.dateAdded,
          notes: fav.notes,
          isLocal: fav.isLocal || false,
          hash: fav.hash,
          product: fav,
        };

        if (fav.isLocal) {
          window.__FAVORITES_CACHE__.localFavoritesCache.set(
            productId,
            favoriteData
          );
          window.__FAVORITES_CACHE__.pendingSyncQueue.add(productId);
        } else {
          window.__FAVORITES_CACHE__.remoteFavoritesCache.set(
            productId,
            favoriteData
          );
        }
      });

      // Restaurar metadata
      window.__FAVORITES_CACHE__.lastRemoteSync = metadata.lastSync || 0;
      window.__FAVORITES_CACHE__.lastLocalModification =
        metadata.lastModification || 0;

      // Restaurar cola de eliminaciones pendientes
      if (metadata.pendingRemove && Array.isArray(metadata.pendingRemove)) {
        metadata.pendingRemove.forEach((productId) => {
          window.__FAVORITES_CACHE__.pendingRemoveQueue.add(
            productId.toString()
          );
        });
      }

      console.log("🔄 Module initialized - loaded from localStorage:", {
        total: favoritesArray.length,
        local: window.__FAVORITES_CACHE__.localFavoritesCache.size,
        remote: window.__FAVORITES_CACHE__.remoteFavoritesCache.size,
        pendingSync: window.__FAVORITES_CACHE__.pendingSyncQueue.size,
        pendingRemove: window.__FAVORITES_CACHE__.pendingRemoveQueue.size,
      });
    }
  } catch (error) {
    console.warn("⚠️ Error initializing cache from localStorage:", error);
  }

  window.__FAVORITES_CACHE__.initialized = true;
}

// Referencias a las variables globales (para compatibilidad con el código existente)
const localFavoritesCache = window.__FAVORITES_CACHE__.localFavoritesCache;
const remoteFavoritesCache = window.__FAVORITES_CACHE__.remoteFavoritesCache;
const pendingSyncQueue = window.__FAVORITES_CACHE__.pendingSyncQueue;
const pendingRemoveQueue = window.__FAVORITES_CACHE__.pendingRemoveQueue;

// 🔧 Helper functions para actualizar valores primitivos en window.__FAVORITES_CACHE__
const getLastRemoteSync = () => window.__FAVORITES_CACHE__.lastRemoteSync;
const setLastRemoteSync = (value) => {
  window.__FAVORITES_CACHE__.lastRemoteSync = value;
};
const getLastLocalModification = () =>
  window.__FAVORITES_CACHE__.lastLocalModification;
const setLastLocalModification = (value) => {
  window.__FAVORITES_CACHE__.lastLocalModification = value;
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const SYNC_RETRY_INTERVAL = 30 * 1000; // 30 segundos
const MAX_SYNC_RETRIES = 3;
const NETWORK_TIMEOUT = 5000; // 5 segundos para timeouts de red

// 🌐 DETECTOR DE CONECTIVIDAD AVANZADO
let networkState = {
  isOnline: navigator.onLine,
  lastCheck: 0,
  consecutiveFailures: 0,
  lastSuccessfulRequest: Date.now(),
};

/**
 * 🔍 Verifica conectividad real haciendo una request pequeña
 */
const checkRealConnectivity = async () => {
  const now = Date.now();

  // Si acabamos de verificar (últimos 10 segundos), usar resultado cached
  if (now - networkState.lastCheck < 10000) {
    return networkState.isOnline;
  }

  try {
    // Request simple y rápida al servidor
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(`${API_CONFIG.V2_BASE_URL}/health`, {
      method: "HEAD",
      signal: controller.signal,
      cache: "no-cache",
    });

    clearTimeout(timeoutId);

    const isConnected = response.ok || response.status < 500;
    networkState.isOnline = isConnected;
    networkState.lastCheck = now;
    networkState.consecutiveFailures = isConnected
      ? 0
      : networkState.consecutiveFailures + 1;

    if (isConnected) {
      networkState.lastSuccessfulRequest = now;
    }

    return isConnected;
  } catch (error) {
    networkState.isOnline = false;
    networkState.lastCheck = now;
    networkState.consecutiveFailures++;

    console.log("🌐 Network check failed:", error.message);
    return false;
  }
};

/**
 * 🎯 Detecta si el error es por conectividad
 */
const isNetworkError = (error) => {
  const networkErrorPatterns = [
    "ERR_NAME_NOT_RESOLVED",
    "ERR_INTERNET_DISCONNECTED",
    "ERR_NETWORK_CHANGED",
    "fetch",
    "NetworkError",
    "Failed to fetch",
  ];

  return networkErrorPatterns.some(
    (pattern) =>
      error.message?.includes(pattern) || error.name?.includes(pattern)
  );
};

// Almacén de metadatos para validación de integridad
const METADATA_STORE = {
  hashCache: new Map(), // product_id -> hash de validación
  syncQueue: new Map(), // product_id -> { attempts, lastAttempt, productData }
};

/**
 * Genera hash básico para validación de integridad del producto
 */
const generateProductHash = (product) => {
  if (!product?.id) return null;
  const baseString = `${product.id}-${product.name}-${
    product.price
  }-${Date.now()}`;
  return btoa(baseString).slice(0, 16); // Hash simple pero efectivo
};

/**
 * 🔧 Valida que el producto existe - MEJORADO para manejo offline
 */
const validateProductIntegrity = async (product, token) => {
  // Debug: Log para entender qué datos están llegando
  console.log("🔍 Validating product:", {
    productId: product?.id,
    productName: product?.name,
    hasToken: !!token,
    networkState: networkState.isOnline,
    productKeys: product ? Object.keys(product) : "no product",
  });

  if (!product?.id || !token) {
    console.warn("⚠️ Validation failed - missing data:", {
      hasProductId: !!product?.id,
      hasToken: !!token,
      product: product,
    });
    return { valid: false, reason: "missing_data" };
  }

  // 🌐 VERIFICAR CONECTIVIDAD ANTES DE HACER LA REQUEST
  const isConnected = await checkRealConnectivity();

  if (!isConnected) {
    console.log(
      "📴 No connectivity - skipping validation, allowing optimistic add"
    );
    return {
      valid: true,
      reason: "offline_optimistic",
      requiresSync: true,
    };
  }

  try {
    // Verificar que el producto existe y está disponible
    const url = `${API_CONFIG.BIENES_URL}/products/${product.id}`;
    console.log("🌐 Making validation request to:", url);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    console.log("📡 Validation response status:", response.status);

    if (!response.ok) {
      if (response.status === 404) {
        console.log("❌ Product not found (404)");
        return { valid: false, reason: "product_not_found" };
      }
      if (response.status === 401 || response.status === 403) {
        console.log("🔒 Unauthorized (401/403)");
        return { valid: false, reason: "unauthorized" };
      }
      console.log("⚠️ Server error:", response.status);
      return { valid: false, reason: "server_error" };
    }

    const productData = await response.json();
    console.log("📄 Product validation response:", {
      success: productData.success,
      enabled: productData.data?.enabled,
      sale: productData.data?.sale,
      id: productData.data?.id,
    });

    if (!productData.success) {
      console.log("❌ Invalid response structure");
      return { valid: false, reason: "invalid_response" };
    }

    const isEnabled = productData.data?.enabled === true;
    const isAvailable = productData.data?.sale === true;

    if (!isEnabled) {
      console.log("⚠️ Product is disabled");
      return { valid: false, reason: "product_disabled" };
    }

    if (!isAvailable) {
      console.log("⚠️ Product is not for sale");
      return { valid: false, reason: "product_not_for_sale" };
    }

    console.log("✅ Product validation passed");
    networkState.lastSuccessfulRequest = Date.now();
    return { valid: true, reason: "valid" };
  } catch (error) {
    console.warn("🔍 Product validation error:", error.message);

    // 🎯 Si es error de red, permitir agregar optimísticamente
    if (isNetworkError(error)) {
      console.log("📴 Network error detected - allowing optimistic add");
      networkState.isOnline = false;
      return {
        valid: true,
        reason: "network_error_optimistic",
        requiresSync: true,
      };
    }

    return { valid: false, reason: "validation_error" };
  }
};

/**
 * Hook avanzado de favoritos con cache híbrido y sincronización inteligente
 */
export const useFreshFavorites = () => {
  const { user, token } = useAuth();
  const [favorites, setFavorites] = useState(
    new Map(JSON.parse(localStorage.getItem("tattersall-favorites")) || [])
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ totalFavorites: 0, recentFavorites: 0 });
  const [syncStatus, setSyncStatus] = useState({ pending: 0, errors: 0 });
  const [isInitialized, setIsInitialized] = useState(false); // 🔥 NUEVO ESTADO
  const isInitializedRef = useRef(false);
  const syncIntervalRef = useRef(null);

  // Función para sincronizar con localStorage
  const syncWithLocalStorage = useCallback(
    (favoritesMap) => {
      try {
        const favoritesArray = Array.from(favoritesMap.values()).map(
          (fav) => fav.product
        );

        // 🔧 NUEVO: Persistir también las colas de sincronización
        localStorage.setItem(
          "tattersall-favorites",
          JSON.stringify(favoritesArray)
        );
        localStorage.setItem(
          "tattersall-favorites-metadata",
          JSON.stringify({
            lastSync: getLastRemoteSync(),
            lastModification: getLastLocalModification(),
            pending: pendingSyncQueue.size,
            pendingRemove: Array.from(pendingRemoveQueue), // 🔧 NUEVO: Persistir eliminaciones pendientes
          })
        );

        console.log("✅ Favorites synced to localStorage:", {
          total: favoritesArray.length,
          local: favoritesArray.filter((f) => f.isLocal).length,
          synced: favoritesArray.filter((f) => !f.isLocal).length,
          pendingRemove: pendingRemoveQueue.size, // 🔧 NUEVO: Log eliminaciones pendientes
        });
      } catch (error) {
        console.warn("⚠️ Failed to sync favorites to localStorage:", error);
      }
    },
    [user]
  );

  // 🔧 NUEVA: Función para procesar eliminaciones pendientes
  const processRemoveQueue = useCallback(async () => {
    if (!user || !token || pendingRemoveQueue.size === 0) return;

    console.log(
      "🗑️ Processing remove queue:",
      pendingRemoveQueue.size,
      "items"
    );

    const itemsToRemove = Array.from(pendingRemoveQueue);
    let successCount = 0;
    let errorCount = 0;

    for (const productId of itemsToRemove) {
      try {
        // Intentar eliminar del servidor
        const response = await favoritesService.removeFromFavorites({
          productId: parseInt(productId),
          token,
        });

        if (response.success) {
          // Eliminar de la cola pendiente
          pendingRemoveQueue.delete(productId);
          successCount++;
          console.log("✅ Removed favorite from server:", productId);
        } else {
          throw new Error(response.error || "Remove failed");
        }
      } catch (error) {
        console.error(
          "❌ Error removing favorite from server:",
          productId,
          error
        );

        // 🔧 Para eliminaciones, si falla por errores de red, mantener en cola
        // Si falla por producto no encontrado (404), eliminar de cola (ya no existe)
        if (
          error.message?.includes("404") ||
          error.message?.includes("not found")
        ) {
          console.log(
            "📌 Product not found on server, removing from queue:",
            productId
          );
          pendingRemoveQueue.delete(productId);
        }

        errorCount++;
      }
    }

    console.log("📊 Remove queue processed:", {
      success: successCount,
      errors: errorCount,
      stillPending: pendingRemoveQueue.size,
    });

    // 🔧 ARREGLO: Actualizar estado de sincronización correctamente
    setSyncStatus((prev) => ({
      ...prev,
      pending: pendingSyncQueue.size + pendingRemoveQueue.size,
      errors: errorCount > 0 ? errorCount : 0, // 🔧 Solo mostrar errores actuales, no acumulados
    }));
  }, [user, token]);

  // Función para procesar la cola de sincronización
  const processSyncQueue = useCallback(async () => {
    if (!user || !token) return;

    // 🔧 MEJORA: Procesar ambas colas
    const totalPending = pendingSyncQueue.size + pendingRemoveQueue.size;
    if (totalPending === 0) return;

    console.log("🔄 Processing sync queues:", {
      toAdd: pendingSyncQueue.size,
      toRemove: pendingRemoveQueue.size,
      total: totalPending,
    });

    setSyncStatus((prev) => ({ ...prev, pending: totalPending }));

    // Procesar eliminaciones primero (más rápidas)
    if (pendingRemoveQueue.size > 0) {
      await processRemoveQueue();
    }

    // Luego procesar adiciones (requieren validación)
    if (pendingSyncQueue.size === 0) return;

    const itemsToSync = Array.from(pendingSyncQueue);
    let successCount = 0;
    let errorCount = 0;

    for (const productId of itemsToSync) {
      const localFavorite = localFavoritesCache.get(productId);
      if (!localFavorite?.isLocal) {
        pendingSyncQueue.delete(productId);
        continue;
      }

      try {
        // Validar integridad del producto antes de sincronizar
        const validation = await validateProductIntegrity(
          localFavorite.product,
          token
        );

        if (!validation.valid) {
          console.warn(
            "⚠️ Product failed validation, removing from sync queue:",
            productId,
            validation.reason
          );

          // 🔧 CAMBIO CRÍTICO: Solo remover si es un error definitivo (producto no existe)
          const definitiveErrors = [
            "product_not_found",
            "product_disabled",
            "product_not_for_sale",
          ];

          if (definitiveErrors.includes(validation.reason)) {
            // Solo eliminar si el producto definitivamente no existe o no está disponible
            console.warn(
              "🗑️ Removing favorite - product permanently unavailable:",
              productId
            );

            // Disparar evento para notificar al usuario
            window.dispatchEvent(
              new CustomEvent("productValidationFailed", {
                detail: {
                  reason: validation.reason,
                  productName:
                    localFavorite.product?.name || "Producto desconocido",
                  productId: productId,
                },
              })
            );

            localFavoritesCache.delete(productId);
            pendingSyncQueue.delete(productId);
            METADATA_STORE.hashCache.delete(productId);
            METADATA_STORE.syncQueue.delete(productId);
            errorCount++;
          } else {
            // Para errores de red, server error, etc. - mantener y reintentar más tarde
            console.log(
              "📌 Keeping favorite for later retry:",
              productId,
              validation.reason
            );
            const syncData = METADATA_STORE.syncQueue.get(productId) || {
              attempts: 0,
            };
            syncData.attempts++;
            syncData.lastAttempt = Date.now();
            syncData.lastError = validation.reason;
            METADATA_STORE.syncQueue.set(productId, syncData);

            // NO eliminar de pendingSyncQueue - mantener para reintentos
            errorCount++;
          }
          continue;
        }

        // Intentar sincronizar con el backend
        const response = await favoritesService.addToFavorites({
          productId: localFavorite.productId,
          notes: localFavorite.notes,
          token,
        });

        if (response.success) {
          // Actualizar con datos reales del servidor
          const syncedFavorite = {
            ...localFavorite,
            id: response.data.id,
            isLocal: false,
            addedAt: response.data.created_at,
            syncedAt: new Date().toISOString(),
          };

          localFavoritesCache.set(productId, syncedFavorite);
          remoteFavoritesCache.set(productId, syncedFavorite);
          pendingSyncQueue.delete(productId);
          METADATA_STORE.syncQueue.delete(productId);

          successCount++;
          console.log("✅ Synced favorite:", localFavorite.product?.name);
        } else {
          throw new Error(response.error || "Sync failed");
        }
      } catch (error) {
        console.error("❌ Error syncing favorite:", productId, error);

        // 🔧 CAMBIO CRÍTICO: Nunca eliminar por errores de sincronización
        const syncData = METADATA_STORE.syncQueue.get(productId) || {
          attempts: 0,
        };
        syncData.attempts++;
        syncData.lastAttempt = Date.now();
        syncData.lastError = error.message;
        METADATA_STORE.syncQueue.set(productId, syncData);

        // 📌 MANTENER en la cola indefinidamente - no eliminar por MAX_SYNC_RETRIES
        console.log(
          `📌 Sync failed (attempt ${syncData.attempts}), will retry later:`,
          productId
        );

        errorCount++;
      }
    }

    // 🔧 ARREGLO CRÍTICO: Resetear errores cuando todo es exitoso
    const currentErrors = errorCount > 0 ? errorCount : 0;
    setSyncStatus({
      pending: pendingSyncQueue.size + pendingRemoveQueue.size,
      errors: currentErrors, // 🔧 Solo errores de esta sesión de sync, no acumulados
    });

    if (successCount > 0 || errorCount > 0) {
      // Actualizar UI y localStorage
      const mergedCache = new Map([
        ...remoteFavoritesCache,
        ...localFavoritesCache,
      ]);
      setFavorites(mergedCache);
      syncWithLocalStorage(mergedCache);
    }

    console.log("📊 Sync completed:", {
      success: successCount,
      errors: errorCount,
      stillPending: pendingSyncQueue.size,
      stillPendingRemove: pendingRemoveQueue.size,
    });
  }, [user, token, syncWithLocalStorage, processRemoveQueue]);

  // Configurar sincronización automática en intervalos
  useEffect(() => {
    if (user && token && pendingSyncQueue.size > 0) {
      // 🔧 ARREGLO: Sincronización más conservadora
      // Solo iniciar intervalo si hay favoritos pendientes por más de 30 segundos
      const delayedSyncSetup = setTimeout(() => {
        if (pendingSyncQueue.size > 0) {
          console.log(
            "⏰ Setting up periodic sync for",
            pendingSyncQueue.size,
            "items"
          );
          syncIntervalRef.current = setInterval(
            processSyncQueue,
            SYNC_RETRY_INTERVAL
          );
        }
      }, 30000); // 30 segundos de delay antes de iniciar sincronización automática

      return () => {
        clearTimeout(delayedSyncSetup);
        if (syncIntervalRef.current) {
          clearInterval(syncIntervalRef.current);
        }
      };
    }
  }, [user, token, processSyncQueue]);

  // Función para cargar favoritos remotos y detectar diferencias
  const loadFreshFavorites = useCallback(
    async (force = false) => {
      if (!user || !token) {
        localFavoritesCache.clear();
        remoteFavoritesCache.clear();
        pendingSyncQueue.clear();
        pendingRemoveQueue.clear();
        setFavorites(new Map());
        setStats({ totalFavorites: 0, recentFavorites: 0 });
        return;
      }

      const now = Date.now();
      if (
        !force &&
        remoteFavoritesCache.size > 0 &&
        now - getLastRemoteSync() < CACHE_DURATION
      ) {
        // Usar cache remoto pero verificar si hay items locales para sincronizar
        if (pendingSyncQueue.size > 0) {
          processSyncQueue(); // No await para no bloquear la UI
        }
        const mergedCache = new Map([
          ...remoteFavoritesCache,
          ...localFavoritesCache,
        ]);
        setFavorites(mergedCache);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log("🔄 Loading fresh favorites from backend...");

        const response = await favoritesService.getUserFavorites({
          token,
          page: 1,
          limit: 100,
        });

        if (response.success) {
          // Procesar favoritos del servidor
          const serverFavoritesMap = new Map();
          const serverProductIds = new Set();

          response.data.favorites.forEach((favorite) => {
            const productId = favorite.product_id.toString();
            serverProductIds.add(productId);

            serverFavoritesMap.set(productId, {
              id: favorite.id,
              productId: favorite.product_id,
              isFavorite: true,
              addedAt: favorite.created_at,
              notes: favorite.notes,
              product: favorite.product,
              isLocal: false,
              hash: generateProductHash(favorite.product),
            });
          });

          // Detectar favoritos locales que no están en el servidor
          const localOnlyFavorites = Array.from(
            localFavoritesCache.keys()
          ).filter(
            (productId) =>
              !serverProductIds.has(productId) &&
              localFavoritesCache.get(productId)?.isLocal
          );

          if (localOnlyFavorites.length > 0) {
            console.log(
              "🔍 Found local favorites not in server:",
              localOnlyFavorites.length
            );
            localOnlyFavorites.forEach((productId) => {
              pendingSyncQueue.add(productId);
            });
            // 🔧 ARREGLO: No procesar sync queue inmediatamente para evitar validaciones masivas
            // Solo marcar para sincronización posterior
            console.log("📌 Local favorites marked for background sync");
          }

          // 🔥 ARREGLO CRÍTICO: Actualizar cache remoto SIN reasignar
          remoteFavoritesCache.clear();
          serverFavoritesMap.forEach((value, key) => {
            remoteFavoritesCache.set(key, value);
          });
          setLastRemoteSync(now);

          // Combinar con favoritos locales
          const mergedCache = new Map([
            ...remoteFavoritesCache,
            ...localFavoritesCache,
          ]);

          setFavorites(mergedCache);
          setStats({
            totalFavorites:
              response.data.meta?.totalFavorites || serverFavoritesMap.size,
            recentFavorites: response.data.meta?.recentFavorites || 0,
          });

          syncWithLocalStorage(mergedCache);

          console.log("✅ Fresh favorites loaded:", {
            server: remoteFavoritesCache.size,
            local: localFavoritesCache.size,
            pending: pendingSyncQueue.size,
            total: mergedCache.size,
          });
        } else {
          throw new Error(response.error || "Error loading favorites");
        }
      } catch (err) {
        console.error("❌ Error loading fresh favorites:", err);
        setError(err.message);

        // Fallback: usar datos combinados del localStorage
        try {
          const savedFavorites = localStorage.getItem("tattersall-favorites");
          if (savedFavorites) {
            const parsed = JSON.parse(savedFavorites);
            const fallbackMap = new Map();

            parsed
              .filter((fav) => fav.userId === user.id)
              .forEach((fav) => {
                const favoriteData = {
                  id: fav.favoriteId,
                  productId: fav.productId,
                  isFavorite: true,
                  addedAt: fav.dateAdded,
                  notes: fav.notes,
                  isLocal: fav.isLocal || false,
                  hash: fav.hash,
                  product: {
                    name: fav.name,
                    slug: fav.slug,
                    price: fav.price,
                    image_urls: [fav.image],
                    lot_category_name: fav.category,
                    ubicacion: fav.ubicacion,
                    brand: fav.marca || "",
                    enabled: true,
                    sale: true,
                  },
                };

                fallbackMap.set(fav.productId.toString(), favoriteData);

                if (fav.isLocal) {
                  // 🔥 ARREGLO: Usar el cache global directamente
                  localFavoritesCache.set(
                    fav.productId.toString(),
                    favoriteData
                  );
                  pendingSyncQueue.add(fav.productId.toString());
                }
              });

            setFavorites(fallbackMap);

            console.log("📦 Loaded favorites from localStorage fallback:", {
              total: fallbackMap.size,
              pending: pendingSyncQueue.size,
            });
          }
        } catch (fallbackError) {
          console.warn(
            "⚠️ Failed to load from localStorage fallback:",
            fallbackError
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [user, token, syncWithLocalStorage, processSyncQueue]
  );

  // Función para verificar si un producto es favorito
  const isFavorite = useCallback((productId) => {
    const id = productId?.toString();
    return localFavoritesCache.has(id) || remoteFavoritesCache.has(id);
  }, []);

  // Toggle favorito con cache-first + sincronización paralela
  const toggleFavorite = useCallback(
    async (product) => {
      if (!user || !token || !product?.id) {
        console.warn(
          "⚠️ Cannot toggle favorite: missing user, token, or product"
        );
        return false;
      }

      const productId = product.id.toString();
      const wasAdded = !isFavorite(productId);

      // 1. Actualización inmediata del cache local (UX first)
      if (wasAdded) {
        const hash = generateProductHash(product);
        const optimisticFavorite = {
          id: `local-${Date.now()}`,
          productId: product.id,
          isFavorite: true,
          addedAt: new Date().toISOString(),
          notes: "",
          isLocal: true, // Marcar como local hasta sincronizar
          hash,
          product,
        };

        localFavoritesCache.set(productId, optimisticFavorite);
        METADATA_STORE.hashCache.set(productId, hash);
        pendingSyncQueue.add(productId);
        setLastLocalModification(Date.now());

        console.log("⚡ Added to local cache (optimistic):", product.name);
      } else {
        // Remover de ambos caches
        const existing =
          localFavoritesCache.get(productId) ||
          remoteFavoritesCache.get(productId);

        localFavoritesCache.delete(productId);
        remoteFavoritesCache.delete(productId);
        pendingSyncQueue.delete(productId);
        METADATA_STORE.hashCache.delete(productId);
        METADATA_STORE.syncQueue.delete(productId);

        // 🔧 ARREGLO: Distinguir entre favoritos locales y remotos
        if (existing) {
          if (existing.isLocal) {
            // Si es local, solo remover de caches (no necesita ir al servidor)
            console.log("⚡ Removed local favorite from cache:", product.name);
          } else {
            // Si es remoto, eliminar del servidor inmediatamente
            console.log(
              "⚡ Removing remote favorite from server:",
              product.name
            );
            favoritesService
              .removeFromFavorites({ productId: product.id, token })
              .then((response) => {
                if (response.success) {
                  console.log(
                    "✅ Successfully removed from server:",
                    product.name
                  );
                } else {
                  console.warn(
                    "⚠️ Failed to remove from server:",
                    response.error
                  );
                  // Si falla, agregar a la cola para reintentar
                  pendingRemoveQueue.add(productId);
                }
              })
              .catch((error) => {
                console.warn("⚠️ Error removing from server:", error);
                // Si hay error de red, agregar a la cola para reintentar
                if (isNetworkError(error)) {
                  pendingRemoveQueue.add(productId);
                }
              });
          }
        }

        console.log("⚡ Removed from cache:", product.name);
      }

      // 2. Actualizar UI inmediatamente
      const mergedCache = new Map([
        ...remoteFavoritesCache,
        ...localFavoritesCache,
      ]);
      setFavorites(mergedCache);
      syncWithLocalStorage(mergedCache);
      setSyncStatus((prev) => ({
        ...prev,
        pending: pendingSyncQueue.size + pendingRemoveQueue.size,
      })); // 🔧 ACTUALIZACIÓN: Incluir ambas colas

      // 3. Sincronización paralela sin bloquear UI
      if (wasAdded) {
        // Validar y sincronizar en background
        validateProductIntegrity(product, token).then((validation) => {
          if (validation.valid) {
            processSyncQueue(); // Sincronizar inmediatamente
          } else {
            console.warn(
              "⚠️ Product failed validation, removing from cache:",
              product.name,
              validation.reason
            );
            localFavoritesCache.delete(productId);
            pendingSyncQueue.delete(productId);
            const updatedCache = new Map([
              ...remoteFavoritesCache,
              ...localFavoritesCache,
            ]);
            setFavorites(updatedCache);
            syncWithLocalStorage(updatedCache);
          }
        });
      } else {
        // 🔧 NUEVO: Procesar eliminaciones en background
        processRemoveQueue();
      }

      // 4. Disparar evento para notificar cambios
      window.dispatchEvent(
        new CustomEvent("favoriteUpdated", {
          detail: {
            productId: product.id,
            action: wasAdded ? "added" : "removed",
            isFavorite: wasAdded,
            isLocal: wasAdded,
          },
        })
      );

      return wasAdded;
    },
    [
      user,
      token,
      isFavorite,
      syncWithLocalStorage,
      processSyncQueue,
      processRemoveQueue,
    ]
  );

  // Función para forzar sincronización manual
  const forceSyncPending = useCallback(async () => {
    await processSyncQueue();
  }, [processSyncQueue]);

  // Función para obtener estado de sincronización
  const getSyncStatus = useCallback(
    () => ({
      pending: pendingSyncQueue.size + pendingRemoveQueue.size, // 🔧 ACTUALIZACIÓN: Incluir ambas colas
      errors: syncStatus.errors,
      lastSync: getLastRemoteSync(),
      isOnline: navigator.onLine,
    }),
    [syncStatus]
  );

  // Cargar favoritos al inicializar
  useEffect(() => {
    console.log("🔄 useFreshFavorites initialization effect:", {
      user: !!user,
      token: !!token,
      isInitializedRef: isInitializedRef.current,
    });

    if (user && token && !isInitializedRef.current) {
      isInitializedRef.current = true;
      setIsInitialized(true);
      // Call loadFreshFavorites directly instead of from dependencie
      loadFreshFavorites(true);
    } else if (!user && !token) {
      // Solo limpiar si estaba inicializado anteriormente
      if (isInitializedRef.current) {
        console.log("🧹 User logged out, cleaning up favorites");
        isInitializedRef.current = false;
        setIsInitialized(false);
        localFavoritesCache.clear();
        remoteFavoritesCache.clear();
        pendingSyncQueue.clear();
        setFavorites(new Map());
        setSyncStatus({ pending: 0, errors: 0 });
        setStats({ totalFavorites: 0, recentFavorites: 0 });
        setError(null);
        setLoading(false);
      } else if (!isInitializedRef.current) {
        // Primera vez sin usuario, solo marcar como inicializado para evitar loops
        setIsInitialized(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, processSyncQueue]); // Intentionally excluding loadFreshFavorites to prevent circular dependency

  // Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  // 🌐 LISTENERS DE CONECTIVIDAD - Agregar al final del hook antes del return
  useEffect(() => {
    // Listener para eventos de conectividad del navegador
    const handleOnlineStatus = async () => {
      const isConnected = await checkRealConnectivity();
      console.log("🌐 Network status changed:", {
        navigatorOnline: navigator.onLine,
        realConnectivity: isConnected,
        pendingSync: pendingSyncQueue.size,
      });

      if (isConnected && pendingSyncQueue.size > 0) {
        console.log("🔄 Connection restored - processing pending favorites");
        setTimeout(() => {
          processSyncQueue();
        }, 1000); // Pequeño delay para asegurar que la conexión esté estable
      }
    };

    const handleOfflineStatus = () => {
      console.log("📴 Network went offline");
      networkState.isOnline = false;
    };

    // Agregar listeners
    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);

    // Verificar estado inicial de conectividad
    if (navigator.onLine && pendingSyncQueue.size > 0) {
      setTimeout(handleOnlineStatus, 500);
    }

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
    };
  }, [processSyncQueue]);

  // 🔄 SINCRONIZACIÓN PERIÓDICA MEJORADA - Verificar pending favorites cada vez que se carga la app
  useEffect(() => {
    if (user && token && isInitialized) {
      // Verificar si hay favoritos pendientes al inicializar
      const checkPendingOnStartup = async () => {
        try {
          const metadata = localStorage.getItem(
            "tattersall-favorites-metadata"
          );
          if (metadata) {
            const parsed = JSON.parse(metadata);

            // 🔧 ARREGLO: Restaurar eliminaciones pendientes
            if (parsed.pendingRemove && Array.isArray(parsed.pendingRemove)) {
              console.log(
                `🗑️ Found ${parsed.pendingRemove.length} pending removals on startup`
              );

              // Restaurar eliminaciones pendientes
              parsed.pendingRemove.forEach((productId) => {
                pendingRemoveQueue.add(productId.toString());
              });
            }

            if (parsed.pending > 0) {
              console.log(
                `🔍 Found ${parsed.pending} pending favorites on startup`
              );

              // Cargar favoritos locales desde localStorage
              const savedFavorites = localStorage.getItem(
                "tattersall-favorites"
              );
              if (savedFavorites) {
                const favoritesArray = JSON.parse(savedFavorites);
                const localOnlyFavorites = favoritesArray.filter(
                  (fav) => fav.isLocal && fav.userId === user.id
                );

                if (localOnlyFavorites.length > 0) {
                  console.log(
                    `📤 Restoring ${localOnlyFavorites.length} local favorites to sync queue`
                  );

                  // Restaurar al cache local y queue de sincronización
                  localOnlyFavorites.forEach((fav) => {
                    const productId = fav.productId.toString();
                    const favoriteData = {
                      id: fav.favoriteId,
                      productId: fav.productId,
                      isFavorite: true,
                      addedAt: fav.dateAdded,
                      notes: fav.notes,
                      isLocal: true,
                      hash: fav.hash,
                      product: {
                        id: fav.productId,
                        name: fav.name,
                        slug: fav.slug,
                        price: fav.price,
                        formattedPrice: fav.formattedPrice,
                        year: fav.year,
                        model: fav.model,
                        images: fav.image ? [fav.image] : [],
                        image_urls: fav.image ? [fav.image] : [],
                        lot_category_name: fav.category,
                        ubicacion: fav.ubicacion,
                        brand: fav.marca || "",
                        enabled: true,
                        sale: true,
                      },
                    };

                    localFavoritesCache.set(productId, favoriteData);
                    pendingSyncQueue.add(productId);
                  });
                }
              }
            }

            // 🔧 ARREGLO: Actualizar UI con todas las colas restauradas
            const mergedCache = new Map([
              ...remoteFavoritesCache,
              ...localFavoritesCache,
            ]);
            setFavorites(mergedCache);
            setSyncStatus((prev) => ({
              ...prev,
              pending: pendingSyncQueue.size + pendingRemoveQueue.size, // 🔧 Incluir ambas colas
            }));

            // 🔧 ARREGLO: Procesar ambas colas si hay conectividad
            const totalPending =
              pendingSyncQueue.size + pendingRemoveQueue.size;
            if (totalPending > 0) {
              console.log("🔄 Total pending operations restored:", {
                toAdd: pendingSyncQueue.size,
                toRemove: pendingRemoveQueue.size,
                total: totalPending,
              });

              // Intentar sincronizar si hay conectividad
              const isConnected = await checkRealConnectivity();
              if (isConnected) {
                console.log("🌐 Connectivity available - starting sync");
                setTimeout(() => {
                  processSyncQueue(); // Esto procesará ambas colas
                }, 2000); // Dar tiempo para que la app se estabilice
              } else {
                console.log(
                  "📴 No connectivity - pending operations will sync when online"
                );
              }
            }
          }
        } catch (error) {
          console.warn(
            "⚠️ Error checking pending favorites on startup:",
            error
          );
        }
      };

      checkPendingOnStartup();
    }
  }, [user, token, isInitialized, processSyncQueue]);

  // 🎯 CLEANUP MEJORADO - Limpiar intervalos al desmontar
  useEffect(() => {
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []);

  return {
    // Estados
    favorites,
    loading,
    error,
    stats,
    syncStatus,
    isInitialized,

    // Funciones principales
    isFavorite,
    toggleFavorite,
    loadFreshFavorites,
    forceSyncPending,
    getSyncStatus,

    // Funciones de utilidad
    refreshFavorites: () => loadFreshFavorites(true),
    clearCache: async () => {
      // Si hay favoritos sincronizados con el servidor y tenemos token, usar bulk delete
      if (user && token && remoteFavoritesCache.size > 0) {
        try {
          setLoading(true);

          // Obtener todos los product_ids de favoritos sincronizados (no locales)
          const syncedProductIds = Array.from(remoteFavoritesCache.values())
            .filter((fav) => !fav.isLocal)
            .map((fav) => parseInt(fav.productId));

          if (syncedProductIds.length > 0) {
            console.log(
              `🗑️ Bulk deleting ${syncedProductIds.length} favorites from server...`
            );

            const bulkResult = await favoritesService.bulkRemoveFromFavorites({
              productIds: syncedProductIds,
              token,
            });

            if (bulkResult.success) {
              console.log(`✅ Bulk delete successful:`, {
                removed: bulkResult.data.removedCount,
                notFound: bulkResult.data.notFoundCount,
                total: bulkResult.data.totalRequested,
              });
            } else {
              console.warn(
                "⚠️ Bulk delete failed, proceeding with cache clear:",
                bulkResult.error
              );
            }
          }
        } catch (error) {
          console.warn(
            "⚠️ Error during bulk delete, proceeding with cache clear:",
            error
          );
        } finally {
          setLoading(false);
        }
      }

      // Limpiar todos los caches y estados
      localFavoritesCache.clear();
      remoteFavoritesCache.clear();
      pendingSyncQueue.clear();
      pendingRemoveQueue.clear(); // 🔧 NUEVO: Limpiar cola de eliminaciones
      METADATA_STORE.hashCache.clear();
      METADATA_STORE.syncQueue.clear();
      setFavorites(new Map());
      setStats({ totalFavorites: 0, recentFavorites: 0 });
      setSyncStatus({ pending: 0, errors: 0 });
      localStorage.removeItem("tattersall-favorites");
      localStorage.removeItem("tattersall-favorites-metadata");

      console.log("🧹 Cache cleared completely");

      // Disparar evento para notificar que se limpiaron los favoritos
      window.dispatchEvent(
        new CustomEvent("favoritesCleared", {
          detail: { timestamp: new Date().toISOString() },
        })
      );
    },

    // Compatibilidad
    getUserFavorites: () =>
      Array.from(favorites.values()).map((f) => {
        return f?.product;
      }),
    getFavoritesCount: () => favorites.size,
  };
};
